import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Autocomplete,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  PersonAddAlt1Rounded,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { keyframes } from "@mui/system";
import { useNavigate } from "react-router-dom";
import { adminService } from "../services/adminService";
import {
  ROUTES,
  buttonGradient,
  buttonGradientHover,
  buttonShadow,
} from "../../../constants/consts";

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const USER_TYPES = [
  { value: "admin", label: "Admin" },
  { value: "destek", label: "Destek" },
  { value: "user", label: "Kullanıcı" },
  { value: "rootUser", label: "Admin Kullanıcı" },
];

const TYPE_NEEDS_SUNUCU = (t) => t === "user" || t === "rootUser";

const normalize = (s = "") => String(s).toLocaleLowerCase("tr-TR").trim();

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    transition: "box-shadow 0.25s ease, background-color 0.25s ease",
    "&:hover": { backgroundColor: "rgba(4,87,129,0.02)" },
    "&.Mui-focused": { boxShadow: "0 0 0 4px rgba(4,87,129,0.10)" },
  },
};

const EMPTY = {
  username: "",
  email: "",
  phone: "",
  pass: "",
  sunucuKodu: "",
  type: "destek",
};

export default function AdminUserAdd() {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [sunucuKoduList, setSunucuKoduList] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: "" });

  useEffect(() => {
    (async () => {
      try {
        const companies = await adminService.getCompanies();
        setSunucuKoduList(companies.map((c) => c.sunucu_kodu).filter(Boolean));
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingOptions(false);
      }
    })();
  }, []);

  const needsSunucu = TYPE_NEEDS_SUNUCU(form.type);

  const handleChange = (k) => (e) =>
    setForm((prev) => ({ ...prev, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.username.trim() || !form.pass.trim()) {
      setError("Kullanıcı adı ve şifre zorunludur.");
      return;
    }
    if (needsSunucu && !form.sunucuKodu) {
      setError("Bu kullanıcı tipi için sunucu kodu zorunludur.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await adminService.addUser({
        username: form.username.trim(),
        phone: form.phone.trim(),
        pass: form.pass,
        email: form.email.trim(),
        sunucuKodu: form.sunucuKodu,
        type: form.type,
      });
      setSnack({ open: true, msg: "Kullanıcı başarıyla eklendi." });
      setForm(EMPTY);
      setTimeout(() => navigate(ROUTES.ADMIN_USERS), 900);
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.message ||
        err?.response?.data ||
        "Kullanıcı eklenemedi.";
      setError(typeof msg === "string" ? msg : "Kullanıcı eklenemedi.");
    } finally {
      setSaving(false);
    }
  };

  const typeLabel = (v) => USER_TYPES.find((t) => t.value === v)?.label || v;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        gap: 1.5,
      }}
    >
      {/* Başlık */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexShrink: 0,
          animation: `${fadeInUp} 0.4s ease-out both`,
        }}
      >
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255,255,255,0.14)",
            border: "1px solid rgba(255,255,255,0.20)",
          }}
        >
          <PersonAddAlt1Rounded sx={{ color: "#fff", fontSize: 20 }} />
        </Box>
        <Box>
          <Typography
            variant="h6"
            fontWeight={800}
            color="#fff"
            sx={{ fontSize: 18 }}
          >
            Yeni Kullanıcı Ekle
          </Typography>
          <Typography
            sx={{
              color: "rgba(255,255,255,0.7)",
              fontSize: 11.5,
              fontWeight: 600,
            }}
          >
            Sisteme yeni bir kullanıcı tanımlayın
          </Typography>
        </Box>
      </Box>

      {/* Form */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          overflowY: "auto",
          pt: 1,
          pb: 2,
        }}
      >
        <Paper
          elevation={0}
          component="form"
          onSubmit={handleSubmit}
          sx={{
            p: { xs: 2.5, sm: 4 },
            borderRadius: 3,
            backgroundColor: "rgba(255,255,255,0.98)",
            boxShadow: "0 12px 32px rgba(0,0,0,0.20)",
            border: "1px solid rgba(255,255,255,0.30)",
            width: "100%",
            maxWidth: { xs: "100%", sm: 900, md: 1100 },
            animation: `${fadeInUp} 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.1s backwards`,
          }}
        >
          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: { xs: 2, sm: 2.5 },
            }}
          >
            <TextField
              label="Kullanıcı Adı *"
              value={form.username}
              onChange={handleChange("username")}
              fullWidth
              sx={inputSx}
            />
            <TextField
              label="Email"
              value={form.email}
              onChange={handleChange("email")}
              fullWidth
              sx={inputSx}
            />
            <TextField
              label="Telefon"
              value={form.phone}
              onChange={handleChange("phone")}
              fullWidth
              sx={inputSx}
            />
            {/* Kullanıcı Tipi */}
            <Autocomplete
              size="small"
              options={USER_TYPES.map((t) => t.value)}
              value={form.type}
              onChange={(_, v) =>
                setForm((prev) => ({
                  ...prev,
                  type: v || "destek",
                  sunucuKodu: TYPE_NEEDS_SUNUCU(v) ? prev.sunucuKodu : "",
                }))
              }
              getOptionLabel={typeLabel}
              disableClearable
              renderOption={(props, opt) => (
                <li {...props}>{typeLabel(opt)}</li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Kullanıcı Tipi *" sx={inputSx} />
              )}
            />

            {/* Sunucu Kodu */}
            <Autocomplete
              size="small"
              options={sunucuKoduList}
              value={form.sunucuKodu || null}
              onChange={(_, v) =>
                setForm((prev) => ({ ...prev, sunucuKodu: v || "" }))
              }
              disabled={!needsSunucu || loadingOptions}
              filterOptions={(opts, { inputValue }) =>
                opts.filter((o) => normalize(o).includes(normalize(inputValue)))
              }
              noOptionsText="Sunucu bulunamadı"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Sunucu Kodu"
                  placeholder={
                    needsSunucu ? "Yaz veya seç" : "Bu tip için gerekli değil"
                  }
                  helperText={
                    !needsSunucu ? "Sadece müşteri tipleri için geçerli" : ""
                  }
                  sx={inputSx}
                />
              )}
            />

            {/* Şifre */}
            <TextField
              label="Şifre *"
              type={showPass ? "text" : "password"}
              value={form.pass}
              onChange={handleChange("pass")}
              fullWidth
              sx={{ ...inputSx, gridColumn: { xs: "1", sm: "1 / span 2" } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowPass((v) => !v)}
                    >
                      {showPass ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box
            sx={{
              mt: 4,
              display: "flex",
              gap: 1.5,
              justifyContent: "flex-end",
            }}
          >
            <Button
              type="button"
              variant="outlined"
              onClick={() => navigate(ROUTES.ADMIN_USERS)}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                px: 3,
                py: 1.2,
                fontWeight: 600,
              }}
            >
              Vazgeç
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                fontWeight: 700,
                px: 4,
                py: 1.2,
                background: buttonGradient,
                boxShadow: buttonShadow,
                "&:hover": { background: buttonGradientHover },
                "&:disabled": { background: "#c7c7c7" },
              }}
            >
              {saving ? (
                <CircularProgress size={20} sx={{ color: "#fff" }} />
              ) : (
                "Kaydet"
              )}
            </Button>
          </Box>
        </Paper>
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={2500}
        onClose={() => setSnack({ open: false, msg: "" })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" sx={{ borderRadius: 2 }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
